import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotificationStore } from '@/store/notification.store';
import { useNotifications } from '@/hooks/useNotifications';
import { NotificationItem } from './NotificationItem';

import Button, { IconButton } from '../common/Button';
import FaIcon from '../common/FaIcon';
import EmptyState from '../common/EmptyState';
import Alert from '../common/Alert';

interface NotificationPanelProps {
  position?: 'below' | 'above';
}

export function NotificationPanel({ position = 'below' }: NotificationPanelProps) {
  const { notifications, isPanelOpen, unreadCount, closePanel, isLoading, error, hasLoadedOnce } =
    useNotificationStore();
  const { markAsRead, markAllAsRead, dismiss, fetchNotifications } = useNotifications();
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Close panel when clicking outside
  // Uses a delayed listener registration to prevent the opening click from
  // immediately triggering the close handler (race between bell toggle and
  // click-outside detection).
  useEffect(() => {
    if (!isPanelOpen) return;

    function handleClickOutside(event: MouseEvent) {
      const target = event.target as HTMLElement;
      // Ignore clicks on the bell button or its children
      if (target.closest?.('[data-notification-bell]')) return;
      // Ignore clicks inside ANY notification panel (there are multiple instances)
      if (target.closest?.('[data-notification-panel]')) return;
      closePanel();
    }

    // Delay registration by one frame so the click/mousedown event that
    // opened the panel does not immediately close it.
    const raf = requestAnimationFrame(() => {
      document.addEventListener('mousedown', handleClickOutside);
    });

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPanelOpen, closePanel]);

  // Close panel on escape key
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        closePanel();
      }
    }

    if (isPanelOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isPanelOpen, closePanel]);

  if (!isPanelOpen) return null;

  const positionClasses = position === 'above'
    ? 'bottom-full mb-2 right-0'
    : 'top-full mt-2 right-0';

  return (
    <div
      ref={panelRef}
      data-notification-panel
      className={`absolute ${positionClasses} w-[calc(100vw-2rem)] sm:w-96 max-h-[70vh] bg-white dark:bg-[#1a2538] rounded-lg shadow-xl border border-gray-200 dark:border-white/10 overflow-hidden z-50`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-white/10">
        <h3 className="heading-3">
          Notifications
          {unreadCount > 0 && (
            <span className="ml-2 text-sm font-normal text-theme-tertiary">
              ({unreadCount} unread)
            </span>
          )}
        </h3>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllAsRead}
              className="text-primary-600 hover:text-primary-700 dark:text-primary-400"
            >
              Mark All Read
            </Button>
          )}
          <IconButton
            icon={<FaIcon icon="xmark" className="w-5 h-5" />}
            label="Close notifications"
            variant="ghost"
            size="sm"
            onClick={closePanel}
          />
        </div>
      </div>

      {/* Notifications List */}
      <div className="overflow-y-auto max-h-[calc(70vh-60px)]">
        {/*
          Branch order matters. "You're all caught up" is an affirmative claim that
          the SERVER said there is nothing waiting — it may only be shown after a
          fetch actually succeeded (hasLoadedOnce) and is not currently failing.
          Otherwise an outage / 429 / expired-tenant 403 reads as a healthy, empty
          inbox and the operator stops checking for queued evacuation orders.
        */}
        {notifications.length === 0 && !error && (isLoading || !hasLoadedOnce) ? (
          <div className="flex items-center justify-center py-12">
            <svg
              className="animate-spin h-6 w-6 text-primary-500"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
        ) : notifications.length === 0 && error ? (
          <div className="p-3">
            <Alert
              variant="danger"
              title="Couldn't load notifications"
              action={
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => fetchNotifications()}
                  disabled={isLoading}
                >
                  {isLoading ? 'Retrying...' : 'Retry'}
                </Button>
              }
            >
              {error} Alerts may be queued that are not shown here.
            </Alert>
          </div>
        ) : notifications.length === 0 ? (
          <EmptyState
            icon="bell"
            title="No notifications yet"
            description="You're all caught up"
            size="sm"
          />
        ) : (
          <>
            {error && (
              <div className="p-3 pb-0">
                <Alert variant="warning" compact hideIcon={false}>
                  Showing last known notifications — refresh failed.
                </Alert>
              </div>
            )}
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onMarkAsRead={markAsRead}
                onDismiss={dismiss}
              />
            ))}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-gray-200 dark:border-white/10 px-4 py-2.5 flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            closePanel();
            navigate('/my-notifications');
          }}
          className="text-primary-600 hover:text-primary-700 dark:text-primary-400"
        >
          View All
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => fetchNotifications()}
          disabled={isLoading}
        >
          {isLoading ? 'Refreshing...' : 'Refresh'}
        </Button>
      </div>
    </div>
  );
}
